import { FC } from "react";

import {
  CardBarGraphContentTemplate,
  CardEmptyGraphContentMessageTemplate,
  CardNumericComparisonContentTemplate,
  CardPieContentTemplate,
  CardUnauthenticatedContentTemplate,
  CardUnimplementedContentTemplate,
} from "@ryde/controls";
import {
  CardNameToColorSchemeMap,
  CardTypeToContentMap,
  CardTypeToSettingsMap,
  DashboardCardNameEnum,
  DashboardCardTemplateTypeEnum,
  IBarGraphContent,
  IBarGraphSettings,
  INumericComparisonCardContent,
  INumericComparisonCardSettings,
  IPieGraphContent,
  IPieGraphSettings,
} from "@ryde/shared";

export interface IContentProps<T extends DashboardCardTemplateTypeEnum> {
  isAuthorized: boolean;
  cardName?: DashboardCardNameEnum;
  cardType?: T;
  content?: CardTypeToContentMap[T];
  settings?: CardTypeToSettingsMap[T];
}

export const DashboardCardContent: FC<
  IContentProps<DashboardCardTemplateTypeEnum>
> = ({
  isAuthorized,
  cardType = DashboardCardTemplateTypeEnum.NONE,
  cardName = DashboardCardNameEnum.NONE,
  content = undefined,
  settings = undefined,
}) => {
  const colorScheme = CardNameToColorSchemeMap[cardName];

  const isBars =
    cardType === DashboardCardTemplateTypeEnum.VERTICAL_BARS ||
    cardType === DashboardCardTemplateTypeEnum.HORIZONTAL_BARS;

  const isPie = cardType === DashboardCardTemplateTypeEnum.PIE_CHART;

  const isNumeric =
    cardType === DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON;

  const isEmpty =
    (isBars && (content as IBarGraphContent)?.entities.length === 0) ||
    (isPie && (content as IPieGraphContent)?.arcs.length === 0);

  const isOther = !(isBars || isNumeric || isPie);

  return (
    <>
      {!isAuthorized && (
        <CardUnauthenticatedContentTemplate
          cardType={cardType}
          cardName={cardName}
        />
      )}
      {isAuthorized && isEmpty && (
        <CardEmptyGraphContentMessageTemplate
          cardType={cardType}
          cardName={cardName}
        />
      )}
      {isAuthorized && !isEmpty && isBars && (
        <CardBarGraphContentTemplate
          cardType={cardType}
          cardName={cardName}
          colorScheme={colorScheme}
          content={content as IBarGraphContent}
          settings={settings as IBarGraphSettings}
        />
      )}
      {isAuthorized && !isEmpty && isPie && (
        <CardPieContentTemplate
          cardType={cardType}
          cardName={cardName}
          colorScheme={colorScheme}
          content={content as IPieGraphContent}
          settings={settings as IPieGraphSettings}
        />
      )}
      {isAuthorized && isNumeric && (
        <CardNumericComparisonContentTemplate
          cardType={cardType}
          cardName={cardName}
          content={content as INumericComparisonCardContent}
          settings={settings as INumericComparisonCardSettings}
        />
      )}
      {isAuthorized && isOther && (
        <CardUnimplementedContentTemplate
          cardType={cardType}
          cardName={cardName}
        />
      )}
    </>
  );
};
